import {Type} from 'class-transformer';
import moment, {Moment} from "moment";
import {OperatingSystem, ProductState} from '../../../enums';
import {NewsFeedRes} from './news.contract';
import {TranslationReq, TranslationRes} from './core';
import {FileUploadRes, MediaType} from '@yasc/yasc-material-components';


export class LicenseDiscountRes {
    public id?: number;
    public amount!: number;
    public discountPercent!: number;

    constructor(options: LicenseDiscountRes) {
        if (options === undefined) return;

        const {
            id = this.id,
            amount = this.amount,
            discountPercent = this.discountPercent,
        } = options;

        this.id = id;
        this.amount = amount;
        this.discountPercent = discountPercent;
    }

}

export class PriceTrendRes {
    public year!: number;
    public price!: number;
}

export class ProductDetailMinRes {
    public id!: string;
    public name!: string;
    public isDemo!: boolean;

    @Type(() => TranslationRes)
    public description!: TranslationRes;

    @Type(() => TranslationRes)
    public shortDescription!: TranslationRes;

    public latestVersion: string = "";

    public productImage?: FileUploadRes

    private _lastReleaseDate?: any;

    get lastReleaseDate(): any {
        return this._lastReleaseDate;
    }

    set lastReleaseDate(value: any) {
        this._lastReleaseDate = moment(value);
    }

}

export class ProductDetailsCollectionRes {
    @Type(() => ProductDetailsRes)
    data: ProductDetailsRes[] = [];

    entries!: number;
}

export class ProductDetailsRes {
    public id: string = "";
    public name: string = "";
    public demoAvailable!: boolean;
    public fullVersionAvailable!: boolean;
    public state!: ProductState;

    @Type(() => TranslationRes)
    public description!: TranslationRes;

    @Type(() => PriceTrendRes)
    public priceTrends: PriceTrendRes[] = [];

    @Type(() => LicenseDiscountRes)
    public discounts: LicenseDiscountRes[] = [];
    public discountsEnabled!: boolean;

    @Type(() => TranslationRes)
    public shortDescription: TranslationRes = new TranslationRes()
    public availableForDevices!: number;
    public paymentInterval!: number;
    public latestVersion: string | null = null;

    @Type(() => FileUploadRes)
    public productImage: FileUploadRes = new FileUploadRes()

    @Type(() => FeatureDetailRes)
    public features: FeatureDetailRes[] = [];

    @Type(() => ProductReleaseRes)
    public releases: ProductReleaseRes[] = [];

    @Type(() => FileUploadRes)
    public licenseAgreements: FileUploadRes[] = [];

    @Type(() => NewsFeedRes)
    public news: NewsFeedRes[] = [];

    @Type(() => FileUploadRes)
    public screenshots: FileUploadRes[] = []

    public availableVersions: string[] = [];

    private _lastReleaseDate?: any;

    get lastReleaseDate(): any {
        return this._lastReleaseDate;
    }

    public availableFrom?: any;
    public availableTo?: any;


    licenseForLanguage(culture: any): FileUploadRes | null {
        const license = this.licenseAgreements.filter(x => x.language == culture);
        if (license.length == 1)
            return license[0];
        else if (this.licenseAgreements.length >= 1)
            return this.licenseAgreements[0];

        return null;
    }

    // TODO: move to backend
    getAllAttachments(): FileUploadRes[] {
        let attachments: FileUploadRes[] = []
        this.news.forEach(e => {
            if (e.downloadLinks !== undefined && e.downloadLinks.length > 0) {
                // exclude images
                let filtered = e.downloadLinks.filter(x =>
                    x.type != MediaType.NewsFeedImage &&
                    x.type != MediaType.ProductScreenshot &&
                    x.type != MediaType.ProductImage &&
                    x.type != MediaType.ProfileImage
                )

                if (filtered.length > 0)
                    attachments.push(...filtered);
            }
        })

        // order by name
        attachments = attachments.sort((a, b) =>
            (a.displayName.toLowerCase() > b.displayName.toLowerCase()) ? 1 : ((b.displayName.toLowerCase() > a.displayName.toLowerCase()) ? -1 : 0));
        return attachments;
    }

    screenshotsForLanguage(culture: any, includeUniversal: boolean = false): FileUploadRes[] {
        if (this.screenshots.length <= 0) return [];
        return this.screenshots.filter(x => x.language == culture || includeUniversal ? (x.language == undefined) : null);
    }

    set lastReleaseDate(value: any) {
        this._lastReleaseDate = moment(value);
    }

    static compare(o1: ProductDetailsRes, o2: ProductDetailsRes) {
        return (o1?.id ?? -1) == (o2?.id ?? -1);
    }
}

export class FeatureDetailRes {
    public id!: number;

    @Type(() => TranslationRes)
    public name!: TranslationRes;

    @Type(() => TranslationRes)
    public description!: TranslationRes;

    @Type(() => PriceTrendRes)
    public priceTrends: PriceTrendRes[] = [];

    public orderableInFullVersion: boolean = false;
    public orderableInDemo: boolean = false;

    getPriceFromYear(year: number): number | null {
        let price = null;
        if (this.priceTrends.length <= 0) return price;

        if (this.priceTrends[year] != undefined && this.priceTrends[year] != null) {
            price = this.priceTrends[year].price;
        } else {
            price = this.priceTrends[this.priceTrends.length - 1].price;
        }
        return price;
    }
}

export class ProductFeatureCreateReq {
    public name: TranslationReq;
    public description: TranslationReq;

    @Type(() => PriceTrendRes)
    public priceTrends: PriceTrendRes[] = [];

    public orderableInFullVersion: boolean = false;
    public orderableInDemo: boolean = false;

    constructor() {
        this.name = new TranslationReq();
        this.description = new TranslationReq();
    }

    createEmpty(supportedCultures: string[]) {
        this.name.fill(supportedCultures);
        this.description.fill(supportedCultures);
    }
}

export class ProductReleaseReq {
    public productId!: string;
    public version!: string;
    public operatingSystem!: OperatingSystem;
    public releaseArtifactId!: string;
}

export class ProductReleaseRes {
    public id!: number;
    public version!: string;
    public fileName!: string;
    public sizeInKb!: number;
    public sizeInMb!: number;
    private _uploaded!: any;
    private _releaseDate!: any;
    public operatingSystem!: OperatingSystem;
    public downloadLink!: string;


    get releaseDate(): any {
        return this._releaseDate;
    }

    set releaseDate(value: any) {
        this._releaseDate = moment(value);
    }

    get uploaded(): any {
        return this._uploaded;
    }

    set uploaded(value: any) {
        this._uploaded = moment(value);
    }
}

export class ProductUpdateCreateReq {
    public name: string = "";
    public description!: TranslationReq;
    public shortDescription!: TranslationReq;
    public availableForDevices!: number;
    public paymentInterval: number = 12;
    public priceTrends: PriceTrendRes[] = [];
    public productImage?: string;

    // shall be filled from moment
    public availableFrom!: any;
    public availableTo!: any;

    public demoAvailable!: boolean;
    public fullVersionAvailable!: boolean;

    @Type(() => LicenseDiscountRes)
    public discounts: LicenseDiscountRes[] = [];

    public discountsEnabled!: boolean;

    public features: ProductFeatureCreateReq[] = [];
    public screenshotIds: string[] = []
    public licenseAgreements: string[] | FileUploadRes[] = []
    public state!: ProductState;

    constructor(prod?: ProductDetailsRes) {
        if (prod === undefined) {
            this.description = new TranslationReq();
            this.shortDescription = new TranslationReq();
            return;
        }
        this.availableForDevices = prod.availableForDevices;
        this.paymentInterval = prod.paymentInterval;
        this.priceTrends = prod.priceTrends;
        this.screenshotIds = prod.screenshots.map(x => x.id);
        this.name = prod.name;
        this.discounts = prod.discounts;
        this.discountsEnabled = prod.discountsEnabled;
        // @ts-ignore
        this.description = prod.description;
        // @ts-ignore
        this.shortDescription = prod.shortDescription;
        // @ts-ignore
        this.features = prod.features;
        this.productImage = prod.productImage.link;
        this.availableFrom = prod.availableFrom;
        this.availableTo = prod.availableTo;
        this.demoAvailable = prod.demoAvailable;
        this.fullVersionAvailable = prod.fullVersionAvailable;
        this.state = prod.state;

    }

    public createEmpty(supportedCultures: string[]) {
        this.description.fill(supportedCultures);
        this.shortDescription.fill(supportedCultures);
    }
}


export class ProductArtifactReq {
    productId!: string;
    operatingSystem!: OperatingSystem;
    version!: string;
}